import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    BellRing, CalendarCheck, CalendarDays, CalendarX, Check, CircleUser, ClipboardList,
    FileSignature, Images, MessageSquareText, UsersRound, MessagesSquare, Users,
} from 'lucide-react';
import './Landing.css';
import cardPlanShot from '../../../assets/landing/card-plan.jpg';
import cardVisitsShot from '../../../assets/landing/card-visits.jpg';
import cardDetailsShot from '../../../assets/landing/card-details.jpg';
import cardConsentsShot from '../../../assets/landing/card-consents.jpg';
import bookingShot from '../../../assets/landing/booking-mobile.jpg';
import calendarShot from '../../../assets/landing/calendar-week.png';
import procedurePhoto from '../../../assets/landing/procedure.jpg';
import lipsBefore from '../../../assets/landing/lips-before.jpg';
import lipsAfter from '../../../assets/landing/lips-after.jpg';
import clientPortalShot from '../../../assets/landing/client-portal-plan.jpg';
import clientPortalVisitsShot from '../../../assets/landing/client-portal-visits.jpg';
import clientPortalCareShot from '../../../assets/landing/client-portal-care.jpg';

// One short line each: the page should not open with a wall of complaints.
const PAINS = [
    { icon: MessagesSquare, text: 'Записи губляться між Instagram, Viber і Telegram' },
    { icon: CalendarX, text: "Клієнт не з'явився на прийом, і час залишився незайнятим" },
    { icon: Images, text: 'Фото губляться в галереї телефону, а план процедур - лише на словах' },
    { icon: FileSignature, text: 'Паперові згоди накопичуються, і потрібну вже не знайти' },
];

// Product screens behind a user-driven tab switch (no autoplay: people read, then pick).
// MASTER_TABS: the master's cabinet. CLIENT_TABS: what the master's clients see.
const SHOWCASE_TABS = [
    {
        id: 'card',
        label: 'Картотека',
        shortLabel: 'Картотека',
        icon: ClipboardList,
        title: 'Уся історія клієнта в одній картці',
        // Second level: sections of the client card, same click-to-switch behaviour
        defaultView: 'details',
        views: [
            {
                id: 'details', label: 'Деталі',
                caption: 'Контакти, день народження, джерело і статистика неявок.',
                img: { src: cardDetailsShot, alt: 'Картка клієнтки в Anamnez, вкладка Деталі: контакти, день народження, нотатки' },
            },
            {
                id: 'visits', label: 'Візити',
                caption: 'Усі візити з датами, послугами й нотатками майстра.',
                img: { src: cardVisitsShot, alt: 'Картка клієнтки в Anamnez, вкладка Візити: завершені й заплановані візити' },
            },
            {
                id: 'plan', label: 'План',
                caption: 'Курс процедур зі статусами й нотатками, прогрес видно одразу.',
                img: { src: cardPlanShot, alt: 'Картка клієнтки в Anamnez, вкладка План процедур: 2 з 5 виконано' },
            },
            {
                id: 'consents', label: 'Згоди',
                caption: 'Згоди з е-підписом, без друку й сканування.',
                img: { src: cardConsentsShot, alt: 'Картка клієнтки в Anamnez, вкладка Згоди: три підписані документи' },
            },
        ],
    },
    {
        id: 'calendar',
        label: 'Календар',
        shortLabel: 'Календар',
        icon: CalendarDays,
        title: 'Увесь тиждень на одному екрані',
        bullets: [
            'Записи всіх майстрів, кожен своїм кольором',
            'Новий запис у два кроки: послуга і час, потім клієнт',
            'Графік і перерви враховуються автоматично',
        ],
        img: { src: calendarShot, width: 1605, height: 662, alt: 'Тижневий календар Anamnez із записами клієнтів майстра на тиждень' },
    },
    {
        id: 'booking',
        label: 'Онлайн-запис',
        shortLabel: 'Запис',
        icon: CalendarCheck,
        title: 'Клієнт записується сам, 24/7',
        bullets: [
            'Обирає майстра, послугу і вільний час на вашій сторінці',
            'Зайняті слоти не показуються, подвійних записів не буде',
            'Посилання на сторінку можна додати в Instagram чи Telegram',
        ],
        img: { src: bookingShot, width: 705, height: 1248, alt: 'Сторінка онлайн-запису на телефоні: вибір дати й вільного часу' },
        phone: true,
    },
    {
        id: 'portal',
        label: 'Кабінет клієнта',
        shortLabel: 'Кабінет',
        icon: CircleUser,
        title: 'Уся історія процедур у телефоні клієнта',
        // Same chip pattern as the master's card, shown on a phone (sections in app order)
        defaultView: 'plan',
        views: [
            {
                id: 'visits', label: 'Візити',
                caption: 'Найближчі записи. Перенести чи скасувати візит клієнт може сам, без дзвінка.',
                img: { src: clientPortalVisitsShot, alt: 'Кабінет клієнтки Anamnez на телефоні: деталі візиту з кнопками Перенести і Скасувати' },
            },
            {
                id: 'plan', label: 'План',
                caption: 'Курс процедур від майстра: що вже зроблено і що попереду.',
                img: { src: clientPortalShot, alt: 'Кабінет клієнтки Anamnez на телефоні: план процедур зі статусами виконано та заплановано' },
            },
            {
                id: 'care', label: 'Догляд',
                caption: 'Засоби догляду з порадами майстра, як і коли їх використовувати.',
                img: { src: clientPortalCareShot, alt: 'Кабінет клієнтки Anamnez на телефоні: рекомендовані засоби догляду з порадами' },
            },
        ],
        note: 'Вхід за кодом у SMS чи Telegram, без пароля.',
        phone: true,
    },
];
const CLIENT_SIDE = ['booking', 'portal'];
const MASTER_TABS = SHOWCASE_TABS.filter(t => !CLIENT_SIDE.includes(t.id));
const CLIENT_TABS = SHOWCASE_TABS.filter(t => CLIENT_SIDE.includes(t.id));

const FEATURES = [
    {
        icon: BellRing,
        title: 'SMS-нагадування проти неявок',
        desc: 'Клієнт підтверджує або скасовує візит за посиланням у SMS.',
    },
    {
        icon: Users,
        title: 'Уся база в одному місці',
        desc: "Історія візитів, пошук за телефоном чи ім'ям і бан недобросовісних клієнтів.",
    },
    {
        icon: UsersRound,
        title: 'Команда майстрів',
        desc: 'Кожен майстер має свій вхід і свій графік роботи.',
    },
];

const STEPS = [
    {
        num: '01',
        title: 'Налаштовуєте компанію',
        desc: 'Графік, послуги, майстри. Кожен майстер отримує свій вхід, а сторінка запису готова одразу.',
    },
    {
        num: '02',
        title: 'Клієнти записуються самі',
        desc: 'Через вашу сторінку запису, 24/7, без дзвінків адміністратору.',
    },
    {
        num: '03',
        title: 'Система нагадує і веде картотеку',
        desc: 'SMS-нагадування, план процедур, фото, згоди з е-підписом і засоби догляду збираються в картці клієнта.',
    },
];

const TARIFFS = [
    {
        id: 'free',
        name: 'Free',
        price: '0 ₴',
        period: 'назавжди',
        desc: 'Для соло-майстра: спробуйте картотеку клієнта без ризику.',
        features: [
            '1 майстер',
            'До 20 клієнтів і 20 записів на місяць',
            'Онлайн-запис на вашій сторінці',
            'Кабінет клієнта',
            '1 шаблон згоди з е-підписом',
            '5 SMS-нагадувань у подарунок',
            'До 5 пунктів плану процедур і 50 фото',
            'До 10 засобів догляду',
            'На сторінці запису видно логотип Anamnez',
            'Підтримка email, відповідь до 48 год',
        ],
        cta: 'Спробувати безкоштовно',
        highlight: false,
    },
    {
        id: 'pro',
        name: 'Pro',
        price: '499 ₴',
        period: '/міс',
        desc: 'Для студії чи кабінету з командою до 5 майстрів.',
        featuresIntro: 'Усе з Free, а також:',
        features: [
            'До 5 майстрів',
            'Клієнти й записи без обмежень',
            '30 SMS щомісяця + докупівля',
            'До 10 шаблонів згод',
            'До 1500 фото клієнтів',
            'Засоби догляду без обмежень',
            'Назва студії як відправник SMS (підключаємо за заявкою)',
            'Підтримка email, відповідь до 24 год',
        ],
        cta: 'Обрати Pro',
        highlight: true,
    },
    {
        id: 'business',
        name: 'Business',
        price: '799 ₴',
        period: '/міс',
        desc: 'Для мережі кабінетів чи студії з командою понад 5 майстрів.',
        featuresIntro: 'Усе з Pro, а також:',
        features: [
            'Майстрів без обмежень',
            '200 SMS щомісяця + докупівля',
            'Шаблони згод і фото без обмежень',
            'Персональний менеджер підтримки',
            { text: 'Лист очікування на зайняті слоти', soon: true },
        ],
        cta: 'Обрати Business',
        highlight: false,
    },
];

// FAQ: LANDING_COPY.md позначав три пункти як [PLACEHOLDER: ...]. Два з них
// отримали реальну відповідь (підтримка, дані після відмови від підписки).
// Третій лишається нерендереним, поки немає готового інструменту імпорту:
// [PLACEHOLDER: «Чи можна перенести вже наявну базу клієнтів з Excel чи іншої
//   системи?» — готового масового імпорту з CSV/Excel в продукті немає
//   (перевірено по коду backend/frontend), перенесення зараз можливе тільки
//   вручну через підтримку]
const FAQS = [
    {
        q: 'Що бачить мій клієнт?',
        a: 'У кожного клієнта є окремий кабінет у телефоні: найближчі візити, план процедур від майстра і рекомендації з догляду. Записатись, перенести чи скасувати візит клієнт може сам. Вхід за кодом у SMS чи Telegram, без пароля і без встановлення застосунку.',
    },
    {
        q: 'Чи потрібна банківська картка, щоб почати на Free?',
        a: 'Ні. Free-план не вимагає оплати: реєструєтесь і одразу починаєте працювати з реальною сторінкою запису.',
    },
    {
        q: 'Як відбувається оплата платних тарифів?',
        a: 'Оплата карткою через LiqPay. Після оплати доступ активується автоматично, історію платежів завжди видно у своєму кабінеті.',
    },
    {
        q: 'Чи можна пізніше підвищити чи знизити тариф?',
        a: 'Так, перехід між тарифами доступний у будь-який момент з кабінету компанії.',
    },
    {
        q: 'SMS-нагадування — це окрема оплата?',
        a: 'На Free — 5 SMS у подарунок одноразово, а докупити додаткові SMS можна без обмежень. На Pro та Business включена щомісячна квота SMS, а після її вичерпання так само можна докупити SMS-повідомлення. Оплата поповнення через LiqPay.',
    },
    {
        q: 'Чи безпечно зберігаються підписані клієнтами згоди?',
        a: 'Так, файли згод і підписані PDF зберігаються в хмарному сховищі, доступ до конкретного документа має тільки компанія та клієнт, якому він належить.',
    },
    {
        q: 'Що буде з моїми даними, якщо я скасую підписку?',
        a: 'Дані компанії зберігаються безстроково. Доступ до платних функцій вимикається, але записи, клієнти та історія нікуди не зникають. Повне видалення даних можливе за запитом до підтримки.',
    },
    {
        q: 'Куди звертатись, якщо виникло питання чи проблема?',
        a: <>Пишіть на <a href="mailto:support@anamnez.me">support@anamnez.me</a>, відповідаємо якнайшвидше. У кабінеті є кнопка «Підтримка» в бічному меню, вона відкриває Telegram-чат з нами.</>,
    },
];


// Long tariff lists hide behind a disclosure so Free does not look like the
// heaviest plan; a single leftover item is shown inline instead of collapsed.
const VISIBLE_TARIFF_FEATURES = 5;
const visibleFeatures = list =>
    list.length - VISIBLE_TARIFF_FEATURES >= 2 ? list.slice(0, VISIBLE_TARIFF_FEATURES) : list;
const hiddenFeatures = list =>
    list.length - VISIBLE_TARIFF_FEATURES >= 2 ? list.slice(VISIBLE_TARIFF_FEATURES) : [];

function renderTariffFeature(feat, fi) {
    const isObj = typeof feat === 'object';
    return (
        <li key={fi}>
            <span className="lnd-pricing-card__check" aria-hidden>✓</span>
            <span>
                {isObj ? feat.text : feat}
                {isObj && (
                    <span className="lnd-pricing-card__soon">
                        {' '}({feat.label || 'скоро'})
                    </span>
                )}
            </span>
        </li>
    );
}

export default function Landing() {
    const revealRefs = useRef([]);
    const [openFaq, setOpenFaq] = useState(0);
    const [activeTabs, setActiveTabs] = useState({ master: MASTER_TABS[0].id, client: CLIENT_TABS[0].id });
    // Selected section per tab that has sections (card, client portal)
    const [activeViews, setActiveViews] = useState(() => Object.fromEntries(
        SHOWCASE_TABS.filter(t => t.views).map(t => [t.id, t.defaultView || t.views[0].id])
    ));
    const setView = (tabId, viewId) => setActiveViews(prev => ({ ...prev, [tabId]: viewId }));
    const tabRefs = useRef({});
    // FAQ items are the only reveal-animated elements whose className React also
    // rewrites on every render (the open/closed toggle) - classList.add('revealed')
    // on those nodes gets silently clobbered the next time React re-renders the
    // className string (e.g. clicking ANY faq question), permanently leaving that
    // item at opacity:0. Tracked in state here so 'revealed' is part of the same
    // React-controlled className instead of fighting it. Other reveal-animated
    // elements (features/steps/pricing/pains) have no click-driven className
    // changes, so the plain classList.add below is still fine for them.
    const [revealedFaqIds, setRevealedFaqIds] = useState(() => new Set());

    const rootRef = useRef(null);

    useEffect(() => {
        // Without an observer (or with reduced motion) nothing is hidden: the page
        // simply renders. The hiding class goes on only once reveals can fire.
        const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        if (!('IntersectionObserver' in window) || reduce) return undefined;
        rootRef.current?.classList.add('lnd--reveal');
        const observer = new IntersectionObserver(
            entries => entries.forEach(e => {
                if (!e.isIntersecting) return;
                e.target.classList.add('revealed');
                const faqIndex = e.target.dataset.faqIndex;
                if (faqIndex !== undefined) {
                    setRevealedFaqIds(prev => (prev.has(+faqIndex) ? prev : new Set(prev).add(+faqIndex)));
                }
            }),
            { threshold: 0.12 }
        );
        revealRefs.current.forEach(el => el && observer.observe(el));
        return () => observer.disconnect();
    }, []);

    // Auth pages are separate chunks (App.js): warm them up once the landing is idle,
    // so the signup button opens instantly.
    useEffect(() => {
        const warm = () => {
            import('../../users/Auth/Register');
            import('../../users/Auth/Login');
        };
        if ('requestIdleCallback' in window) {
            const id = window.requestIdleCallback(warm, { timeout: 4000 });
            return () => window.cancelIdleCallback(id);
        }
        const t = setTimeout(warm, 2000);
        return () => clearTimeout(t);
    }, []);

    const ref = el => { if (el && !revealRefs.current.includes(el)) revealRefs.current.push(el); };

    const selectTab = (group, id) => setActiveTabs(prev => ({ ...prev, [group]: id }));

    // Arrow keys move between tabs of one group (WAI-ARIA tabs pattern)
    const onTabKeyDown = (e, group, tabs) => {
        const step = { ArrowRight: 1, ArrowLeft: -1 }[e.key];
        if (!step) return;
        e.preventDefault();
        const i = tabs.findIndex(t => t.id === activeTabs[group]);
        const next = tabs[(i + step + tabs.length) % tabs.length].id;
        selectTab(group, next);
        tabRefs.current[next]?.focus();
    };

    const renderTabs = (group, tabs, label) => {
        const active = activeTabs[group];
        return (
                <div className="lnd-tabs" ref={ref}>
                    <div className="lnd-tabs__list" role="tablist" aria-label={label}>
                        {tabs.map(t => (
                            <button
                                key={t.id}
                                type="button"
                                role="tab"
                                id={`lnd-tab-${t.id}`}
                                aria-selected={active === t.id}
                                aria-controls={`lnd-panel-${t.id}`}
                                tabIndex={active === t.id ? 0 : -1}
                                className="lnd-tabs__tab"
                                ref={el => { tabRefs.current[t.id] = el; }}
                                onClick={() => selectTab(group, t.id)}
                                onKeyDown={e => onTabKeyDown(e, group, tabs)}
                            >
                                <t.icon size={18} strokeWidth={1.75} aria-hidden />
                                <span className="lnd-tabs__label">{t.label}</span>
                                <span className="lnd-tabs__label--short">{t.shortLabel}</span>
                            </button>
                        ))}
                    </div>
                    {/* All panels share one grid cell: no layout jump when switching */}
                    <div className="lnd-tabs__panels">
                        {tabs.map(t => (
                            <div
                                key={t.id}
                                role="tabpanel"
                                id={`lnd-panel-${t.id}`}
                                aria-labelledby={`lnd-tab-${t.id}`}
                                aria-hidden={active !== t.id}
                                className={`lnd-tabs__panel${t.phone ? ' lnd-tabs__panel--phone' : ''}${active === t.id ? ' is-active' : ''}`}
                            >
                                {t.views ? renderViews(t) : (
                                    <>
                                        <div className="lnd-tabs__text">
                                            <h3 className="lnd-feature-card__title">{t.title}</h3>
                                            <ul className="lnd-feature-card__bullets">
                                                {t.bullets.map(b => <li key={b}>{b}</li>)}
                                            </ul>
                                        </div>
                                        <img
                                            className={`lnd-tabs__img lnd-tabs__img--${t.id}`}
                                            src={t.img.src}
                                            width={t.img.width}
                                            height={t.img.height}
                                            loading="lazy"
                                            alt={t.img.alt}
                                        />
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
        );
    };

    // Tab with sections: chips switch the screenshot (wide for the card, phone for the portal)
    const renderViews = t => {
        const current = activeViews[t.id];
        const active = t.views.find(v => v.id === current);
        const [w, h] = t.phone ? [705, 1526] : [1200, 560];
        return (
            <>
                <div className={t.phone ? 'lnd-tabs__text lnd-views__head--phone' : 'lnd-views__head'}>
                    <h3 className="lnd-feature-card__title">{t.title}</h3>
                    <div className="lnd-views__chips" role="group" aria-label={`Розділи: ${t.label}`}>
                        {t.views.map(v => (
                            <button
                                key={v.id}
                                type="button"
                                className="lnd-views__chip"
                                aria-pressed={current === v.id}
                                onClick={() => setView(t.id, v.id)}
                            >
                                {v.label}
                            </button>
                        ))}
                    </div>
                    <p className="lnd-views__caption" aria-live="polite">{active.caption}</p>
                    {t.note && <p className="lnd-views__note">{t.note}</p>}
                </div>
                <div className="lnd-views__shots">
                    {t.views.map(v => (
                        <img
                            key={v.id}
                            className={`lnd-tabs__img lnd-views__img${current === v.id ? ' is-active' : ''}`}
                            src={v.img.src}
                            width={w}
                            height={h}
                            loading="lazy"
                            alt={v.img.alt}
                            aria-hidden={current !== v.id}
                        />
                    ))}
                </div>
            </>
        );
    };

    return (
        <div className="lnd" ref={rootRef}>
            {/* ── Nav ── */}
            <nav className="lnd-nav">
                <div className="lnd-nav__logo">Anamnez</div>
                <div className="lnd-nav__links">
                    <a href="#features" className="lnd-nav__link-a">Можливості</a>
                    <a href="#for-clients" className="lnd-nav__link-a">Для клієнтів</a>
                    <a href="#pricing" className="lnd-nav__link-a">Тарифи</a>
                    <a href="#faq" className="lnd-nav__link-a">FAQ</a>
                </div>
                <div className="lnd-nav__actions">
                    <Link to="/login" className="lnd-nav__link">Увійти</Link>
                    <Link to="/register" className="lnd-nav__cta">
                        <span className="lnd-nav__cta-full">Спробувати безкоштовно</span>
                        <span className="lnd-nav__cta-short">Спробувати</span>
                    </Link>
                </div>
            </nav>

            <main>
            {/* ── Hero ── */}
            <section className="lnd-hero">
                <div className="lnd-hero__bg" />
                <div className="lnd-hero__content">
                    <div className="lnd-hero__text">
                        <div className="lnd-hero__badge">Для косметологів, тату-майстрів і медичних кабінетів</div>
                        <h1 className="lnd-hero__h1">
                            Історія клієнта в <em>одній</em> картці
                        </h1>
                        <p className="lnd-hero__sub">
                            План процедур, фото результатів і згода з е-підписом.
                            Клієнти записуються самі 24/7 і бачать свої візити та план
                            в особистому кабінеті.
                        </p>
                        <div className="lnd-hero__btns">
                            <Link to="/register" className="lnd-btn lnd-btn--primary">
                                Спробувати безкоштовно
                            </Link>
                            <a href="#pricing" className="lnd-btn lnd-btn--ghost">
                                Подивитись тарифи
                            </a>
                        </div>
                        <ul className="lnd-hero__trust">
                            <li><Check size={15} strokeWidth={2} aria-hidden />Free назавжди</li>
                            <li><Check size={15} strokeWidth={2} aria-hidden />Без банківської картки</li>
                        </ul>
                    </div>
                    <div className="lnd-hero__visual">
                        {/* Product on devices: salon app on a laptop, client portal on a phone */}
                        <div className="lnd-laptop">
                            <div className="lnd-laptop__screen">
                                <img
                                    src={cardPlanShot}
                                    width="1200"
                                    height="560"
                                    alt="Anamnez на ноутбуці: список клієнтів і план процедур Олени Ковальчук"
                                    fetchpriority="high"
                                />
                            </div>
                            <div className="lnd-laptop__base" />
                        </div>
                        <img
                            className="lnd-hero__phone"
                            src={clientPortalShot}
                            width="705"
                            height="1526"
                            alt="Кабінет клієнтки Anamnez на телефоні: план процедур"
                        />
                        <div className="lnd-float lnd-float--result" role="group" aria-label="Фото результату: до і після">
                            <div className="lnd-float__label">Фото результату</div>
                            <div className="lnd-float__pair">
                                <figure>
                                    <img src={lipsBefore} width="480" height="480" alt="Губи до процедури" />
                                    <figcaption>До</figcaption>
                                </figure>
                                <figure>
                                    <img src={lipsAfter} width="480" height="480" alt="Губи після процедури" />
                                    <figcaption>Після</figcaption>
                                </figure>
                            </div>
                        </div>
                        <div className="lnd-float lnd-float--sms" aria-hidden>
                            <div className="lnd-float__label">
                                <MessageSquareText size={14} strokeWidth={1.75} />SMS-нагадування
                            </div>
                            Олено, завтра об 11:00 ви записані на Біоревіталізацію.{' '}
                            <span className="lnd-float__link">Підтвердити</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Pains ── */}
            <section className="lnd-pains" id="pains" aria-labelledby="lnd-pains-h">
                <div className="lnd-container">
                    <h2 id="lnd-pains-h" className="lnd-sr-only">Знайомі проблеми</h2>
                    <ul className="lnd-pains__list">
                        {PAINS.map((p, i) => (
                            <li
                                key={p.text}
                                className="lnd-pain"
                                ref={ref}
                                style={{ '--delay': `${i * 60}ms` }}
                            >
                                <p.icon size={22} strokeWidth={1.5} aria-hidden />
                                <span>{p.text}</span>
                            </li>
                        ))}
                    </ul>
                    <p className="lnd-pains__answer" ref={ref}>
                        Anamnez збирає запис, нагадування і картку клієнта в одному кабінеті.
                        А в клієнта є свій: з візитами, планом процедур і доглядом.
                    </p>
                </div>
            </section>

            {/* ── Features ── */}
            <section className="lnd-features" id="features">
                <div className="lnd-container">
                    <h2 className="lnd-section-h2" ref={ref}>
                        Ваш кабінет: картка клієнта і календар
                    </h2>
                    {renderTabs('master', MASTER_TABS, 'Кабінет майстра')}
                    <ul className="lnd-feature-list">
                        {FEATURES.map((f, i) => (
                            <li
                                key={f.title}
                                className="lnd-feature-item"
                                ref={ref}
                                style={{ '--delay': `${i * 60}ms` }}
                            >
                                <span className="lnd-feature-item__icon"><f.icon size={22} strokeWidth={1.5} aria-hidden /></span>
                                <div>
                                    <h3 className="lnd-feature-item__title">{f.title}</h3>
                                    <p className="lnd-feature-item__desc">{f.desc}</p>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* ── Client side: what the master's clients see ── */}
            <section className="lnd-clientside" id="for-clients">
                <div className="lnd-container">
                    <h2 className="lnd-section-h2" ref={ref}>А це бачать ваші клієнти</h2>
                    <p className="lnd-clientside__lead" ref={ref}>
                        Сторінка запису і кабінет клієнта відкриваються в телефоні: без застосунку і без пароля.
                    </p>
                    {renderTabs('client', CLIENT_TABS, 'Що бачить клієнт')}
                </div>
            </section>

            {/* ── How it works ── */}
            <section className="lnd-steps">
                <div className="lnd-container lnd-steps__layout">
                    <img
                        className="lnd-steps__photo"
                        src={procedurePhoto}
                        width="1280"
                        height="1160"
                        loading="lazy"
                        alt="Косметологиня проводить апаратну процедуру обличчя"
                    />
                    <div>
                        <div className="lnd-section-label" ref={ref}>
                            Як це працює
                        </div>
                        <h2 className="lnd-section-h2" ref={ref}>
                            Три кроки до впорядкованого кабінету
                        </h2>
                        <p className="lnd-steps__lead" ref={ref}>
                            Кожен крок займає кілька хвилин. Без інструкцій і без технічного фахівця в штаті.
                        </p>
                        <div className="lnd-steps__list">
                            {STEPS.map((s, i) => (
                                <div
                                    key={i}
                                    className="lnd-step"
                                    ref={ref}
                                    style={{ '--delay': `${i * 100}ms` }}
                                >
                                    <div className="lnd-step__num">{s.num}</div>
                                    <h3 className="lnd-step__title">{s.title}</h3>
                                    <p className="lnd-step__desc">{s.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Pricing ── */}
            <section className="lnd-pricing" id="pricing">
                <div className="lnd-container">
                    <h2 className="lnd-section-h2" ref={ref}>
                        Почніть безкоштовно,<br />платіть, коли виростете
                    </h2>
                    <div className="lnd-pricing__grid">
                        {TARIFFS.map((t, i) => (
                            <div
                                key={t.id}
                                className={`lnd-pricing-card${t.highlight ? ' lnd-pricing-card--highlight' : ''}`}
                                ref={ref}
                                style={{ '--delay': `${i * 100}ms` }}
                            >
                                {t.highlight && (
                                    <div className="lnd-pricing-card__badge">Рекомендовано</div>
                                )}
                                <div className="lnd-pricing-card__name">{t.name}</div>
                                <div className="lnd-pricing-card__price-row">
                                    <span className="lnd-pricing-card__price">{t.price}</span>
                                    <span className="lnd-pricing-card__period">{t.period}</span>
                                </div>
                                {t.priceNote && (
                                    <div className="lnd-pricing-card__price-note">{t.priceNote}</div>
                                )}
                                <p className="lnd-pricing-card__desc">{t.desc}</p>
                                {t.featuresIntro && (
                                    <div className="lnd-pricing-card__intro">{t.featuresIntro}</div>
                                )}
                                <ul className="lnd-pricing-card__features">
                                    {visibleFeatures(t.features).map(renderTariffFeature)}
                                </ul>
                                {hiddenFeatures(t.features).length > 0 && (
                                    <details className="lnd-pricing-card__more">
                                        <summary>Усі можливості тарифу</summary>
                                        <ul className="lnd-pricing-card__features">
                                            {hiddenFeatures(t.features).map(renderTariffFeature)}
                                        </ul>
                                    </details>
                                )}
                                <Link
                                    to="/register"
                                    className={`lnd-btn lnd-btn--block ${t.highlight ? 'lnd-btn--primary' : 'lnd-btn--outline'}`}
                                >
                                    {t.cta}
                                </Link>
                            </div>
                        ))}
                    </div>
                    <p className="lnd-pricing__footnote" ref={ref}>
                        Free без банківської картки. SMS понад квоту можна докупити.
                        Оплата карткою через LiqPay, тариф можна змінити будь-коли.
                    </p>
                </div>
            </section>

            {/* ── FAQ ── */}
            <section className="lnd-faq" id="faq">
                <div className="lnd-container lnd-container--narrow">
                    <h2 className="lnd-section-h2" ref={ref}>Часті запитання</h2>
                    <div className="lnd-faq__list">
                        {FAQS.map((f, i) => {
                            const isOpen = openFaq === i;
                            return (
                                <div
                                    key={i}
                                    data-faq-index={i}
                                    className={`lnd-faq__item${isOpen ? ' open' : ''}${revealedFaqIds.has(i) ? ' revealed' : ''}`}
                                    ref={ref}
                                    style={{ '--delay': `${i * 60}ms` }}
                                >
                                    <button
                                        type="button"
                                        className="lnd-faq__q"
                                        onClick={() => setOpenFaq(isOpen ? -1 : i)}
                                        aria-expanded={isOpen}
                                    >
                                        <span>{f.q}</span>
                                        <span className="lnd-faq__icon" aria-hidden>{isOpen ? '−' : '+'}</span>
                                    </button>
                                    <div className="lnd-faq__a">
                                        <div><p>{f.a}</p></div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ── CTA ── */}
            <section className="lnd-cta">
                <div className="lnd-container">
                    <h2 className="lnd-cta__h2" ref={ref}>
                        Готові прибрати дзвінки<br />та хаос із вашого запису?
                    </h2>
                    <p className="lnd-cta__sub" ref={ref}>
                        Створіть безкоштовний акаунт за кілька хвилин. Банківська картка не потрібна.
                    </p>
                    <Link to="/register" className="lnd-btn lnd-btn--primary lnd-btn--lg" ref={ref}>
                        Спробувати безкоштовно
                    </Link>
                    <p className="lnd-cta__note" ref={ref}>
                        Free-план назавжди · Апгрейд у будь-який момент, коли будете готові
                    </p>
                </div>
            </section>

            </main>

            {/* ── Footer ── */}
            <footer className="lnd-footer">
                <div className="lnd-container">
                    <span className="lnd-footer__logo">Anamnez</span>
                    <a className="lnd-footer__contact" href="mailto:support@anamnez.me">support@anamnez.me</a>
                    <div className="lnd-footer__links">
                        <Link to="/terms" className="lnd-footer__link">Умови використання</Link>
                        <Link to="/privacy" className="lnd-footer__link">Політика конфіденційності</Link>
                    </div>
                    <span className="lnd-footer__copy">© 2026 Anamnez. Всі права захищені.</span>
                </div>
            </footer>
        </div>
    );
}
